import { useEffect, useState } from "react";
import { ShieldAlert } from "lucide-react";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, setResponsibilityInterceptor } from "@/lib/api";
import { toast } from "sonner";

/**
 * Sprint 2H P3 — <ResponsibilityModal>
 *
 * Full-screen "I understand what Opstrait is" acceptance gate. Fires when any
 * API response returns 403 responsibility_acceptance_required. Distinct from
 * ConfirmationGate (per-action) — this is one-time per user per scheme.
 *
 * Mount once at app root (inside AuthProvider so we have active user context).
 */
export default function ResponsibilityModal() {
  const [open, setOpen] = useState(false);
  const [schemeId, setSchemeId] = useState(null);
  const [retry, setRetry] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [sandbox, setSandbox] = useState(false);

  useEffect(() => {
    // Register interceptor callback exposed by /app/frontend/src/lib/api.js.
    setResponsibilityInterceptor(({ scheme_id, retryOriginal }) => {
      setSchemeId(scheme_id);
      setRetry(() => retryOriginal);
      setOpen(true);
    });
    api.get("/health").then((r) => setSandbox(r.data.sandbox_mode === true)).catch(() => {});
    return () => setResponsibilityInterceptor(null);
  }, []);

  const accept = async () => {
    if (!schemeId) { setOpen(false); return; }
    setSubmitting(true);
    try {
      await api.post(`/schemes/${schemeId}/accept-responsibility`, {
        acknowledged: true,
        terms_version: "placeholder-v1",
      });
      toast.success("Responsibility acknowledged");
      setOpen(false);
      if (retry) {
        try { await retry(); } catch {}
      }
    } catch (e) {
      toast.error("Could not record acceptance. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-lg"
        data-testid="responsibility-modal"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: "var(--navy)" }}>
            <ShieldAlert size={18} style={{ color: "var(--blue)" }} />
            Confirm your responsibility
          </DialogTitle>
        </DialogHeader>

        {sandbox && (
          <div
            className="rounded-md p-2 text-[11px] font-bold uppercase tracking-wider text-center"
            style={{ background: "#fee2e2", color: "#991b1b", border: "1px solid #fecaca" }}
            data-testid="responsibility-placeholder-banner"
          >
            Placeholder copy — not lawyer-reviewed — not for pilot use
          </div>
        )}

        <div className="text-sm space-y-3" style={{ color: "var(--navy)" }}>
          <p>
            Opstrait is a self-management assistant for strata schemes. It surfaces
            information, tracks items, and helps chairs run their scheme — but it is not a
            legal or financial adviser and does not act on your scheme's behalf.
          </p>
          <p>
            By continuing, you acknowledge that the chair and committee remain responsible
            for verifying every calculation, meeting notice, levy, and by-law before
            relying on it. Opstrait shows what the Act requires; you decide what applies.
          </p>
        </div>

        <DialogFooter>
          <Button
            onClick={accept}
            disabled={submitting}
            style={{ background: "var(--blue)", color: "white" }}
            className="w-full"
            data-testid="responsibility-accept-button"
          >
            {submitting ? "Recording…" : "I acknowledge and accept responsibility"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
